import React, { useMemo, useState } from 'react';
import '../css/TripList.css';
import '../css/TripListMobile.css';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaChevronDown, FaChevronRight, FaImage, FaLock, FaSearch } from 'react-icons/fa';

const TripList = ({ trips = {}, error }) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({ published: true, planned: true, draft: true, finished: true });

  const mergeAndDeduplicate = (...lists) => {
    const seen = new Set();
    return lists.flat().filter((trip) => {
      if (seen.has(trip.guid)) return false;
      seen.add(trip.guid);
      return true;
    });
  };

  const getTripsForAccessFilter = () => {
    if (!trips) return [];
    switch (activeFilter) {
      case 'created': return trips.createdByMe || [];
      case 'mentor': return trips.asMentor || [];
      case 'coordinator': return trips.asCoordinator || [];
      case 'access': return trips.accessGranted || [];
      default:
        return mergeAndDeduplicate(
          trips.createdByMe || [],
          trips.asMentor || [],
          trips.asCoordinator || [],
          trips.accessGranted || []
        );
    }
  };

  const isFinished = (trip) => trip.endDate && new Date(trip.endDate) < new Date();

  const getStatusKey = (trip) => {
    if (isFinished(trip)) return 'finished';
    if ([3, 5].includes(trip.status)) return 'published';
    if (trip.status === 2) return 'planned';
    if ([1, 4].includes(trip.status)) return 'draft';
    return 'other';
  };

  const allAccessibleTrips = useMemo(() => getTripsForAccessFilter(), [trips, activeFilter]);

  const filteredTrips = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return allAccessibleTrips.filter((trip) => !query || trip.name?.toLowerCase().includes(query));
  }, [allAccessibleTrips, searchQuery]);

  const groupedTrips = useMemo(() => {
    const groups = { published: [], planned: [], draft: [], finished: [] };

    filteredTrips.forEach((trip) => {
      const key = getStatusKey(trip);
      if (groups[key]) groups[key].push(trip);
    });

    groups.published.sort((a, b) => new Date(a.startDate || '9999-12-31') - new Date(b.startDate || '9999-12-31'));
    groups.planned.sort((a, b) => new Date(a.startDate || '9999-12-31') - new Date(b.startDate || '9999-12-31'));
    groups.draft.sort((a, b) => new Date(a.startDate || '9999-12-31') - new Date(b.startDate || '9999-12-31'));
    groups.finished.sort((a, b) => new Date(b.endDate || 0) - new Date(a.endDate || 0));

    return groups;
  }, [filteredTrips]);

  const formatDate = (dateString) => new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(new Date(dateString));

  const getRegistrationClass = (value) => {
    switch (Number(value)) {
      case 1: return 'ribbon--green';
      case 2: return 'ribbon--orange';
      case 3: return 'ribbon--red';
      default: return '';
    }
  };

  const toggleGroup = (key) => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleHeaderKeyDown = (event, key) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleGroup(key);
    }
  };

  const renderTripCard = (trip) => {
    const isInternal = [4, 5].includes(trip.status);
    const showRegistration = getStatusKey(trip) === 'published' && Number(trip.registrationRibbon) > 0;
    const registrationClass = showRegistration ? `has-registration ${getRegistrationClass(trip.registrationRibbon)}` : '';
    const settlementClass = trip.tripSettlementStatus === 4 ? 'settled' : trip.tripSettlementStatus === 5 ? 'archived' : '';

    return (
      <article
        key={`${trip.guid}-${activeFilter}`}
        className={`trip-modern-card ${isFinished(trip) ? 'trip-modern-card--finished' : ''} ${registrationClass} ${settlementClass}`}
        onClick={() => navigate(`/trips/${trip.guid}`)}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') navigate(`/trips/${trip.guid}`);
        }}
      >
        <div className="trip-modern-image-wrap">
          {trip.photoThumbnailLink ? (
            <img src={trip.photoThumbnailLink} alt={`Miniaturka dla ${trip.name}`} className="trip-modern-image" />
          ) : (
            <div className="trip-modern-placeholder"><FaImage /></div>
          )}
          {isInternal && <span className="trip-internal-lock" title="Wycieczka wewnętrzna"><FaLock /></span>}
        </div>

        <div className="trip-modern-content">
          <h3>{trip.name}</h3>
          {trip.startDate && trip.endDate ? (
            <div className="trip-modern-date"><FaCalendarAlt /><span>{formatDate(trip.startDate)} – {formatDate(trip.endDate)}</span></div>
          ) : (
            <div className="trip-modern-date trip-modern-date--muted"><FaCalendarAlt /><span>Termin nieustalony</span></div>
          )}
        </div>
      </article>
    );
  };

  const groupDefinitions = [
    ['published', 'Opublikowane'],
    ['planned', 'Planowane'],
    ['draft', 'Nieopublikowane'],
  ];

  if (error) return <p className="error-message">{error}</p>;

  return (
    <section className="trip-browser">
      <div className="trip-toolbar">
        <label className="trip-search-box">
          <FaSearch />
          <input type="text" placeholder="Szukaj po nazwie wycieczki..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </label>

        <label className="trip-access-filter">
          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
            <option value="all">Wszystkie dostępne</option>
            <option value="created">Utworzone przeze mnie</option>
            <option value="mentor">Mentorowane</option>
            <option value="coordinator">Koordynowane</option>
            <option value="access">Z udzielonym dostępem</option>
          </select>
          <FaChevronDown />
        </label>
      </div>

      {filteredTrips.length === 0 ? (
        <div className="no-trips-message">
          <FaSearch />
          <strong>Brak wycieczek do wyświetlenia</strong>
          <span>Zmień filtr lub wyszukiwaną frazę.</span>
        </div>
      ) : (
        <>
          <div className="trip-groups-grid">
            {groupDefinitions.map(([key, label]) => (
              <section key={key} className={`trip-status-group ${expandedGroups[key] ? '' : 'trip-status-group--collapsed'}`}>
                <div className="trip-group-header" role="button" tabIndex={0} onClick={() => toggleGroup(key)} onKeyDown={(event) => handleHeaderKeyDown(event, key)}>
                  <span className="trip-group-title">
                    {expandedGroups[key] ? <FaChevronDown /> : <FaChevronRight />}
                    <span>{label}</span>
                  </span>
                </div>

                {expandedGroups[key] && (
                  <div className="trip-group-list">
                    {groupedTrips[key].length > 0
                      ? groupedTrips[key].map(renderTripCard)
                      : <div className="trip-group-empty">Brak wycieczek</div>}
                  </div>
                )}
              </section>
            ))}
          </div>

          <section className={`trip-finished-section ${expandedGroups.finished ? '' : 'trip-status-group--collapsed'}`}>
            <div className="trip-group-header trip-group-header--finished" role="button" tabIndex={0} onClick={() => toggleGroup('finished')} onKeyDown={(event) => handleHeaderKeyDown(event, 'finished')}>
              <span className="trip-group-title">
                {expandedGroups.finished ? <FaChevronDown /> : <FaChevronRight />}
                <span>Zakończone wycieczki</span>
              </span>
            </div>

            {expandedGroups.finished && (
              <div className="trip-finished-grid">
                {groupedTrips.finished.length > 0
                  ? groupedTrips.finished.map(renderTripCard)
                  : <div className="trip-group-empty trip-group-empty--wide">Brak zakończonych wycieczek</div>}
              </div>
            )}
          </section>
        </>
      )}
    </section>
  );
};

export default TripList;
